import { callable } from './callable.js'

export interface FamilyLearnerRow {
  workspaceId: string
  workspaceName: string
  learnerId: string
  displayName: string
  photoUrl: string | null
  relation: 'GUARDIAN' | 'SELF'
  frozen: boolean
  classNames: string[]
  isStaffHere: boolean
  trainingProgrammes: Array<{ rootId: string; title: string }>
  isMinor?: boolean
}

export interface ListMyFamilyLearnersResult {
  learners: FamilyLearnerRow[]
}

export interface GetFamilyTodayInput {
  workspaceId: string
  learnerId: string
}

export interface FamilyTodaySession {
  seriesId: string
  sessionId: string
  nodeId: string
  classId: string
  title: string
  startTime: string
  durationMin: number
  status: string
  substituteName: string | null
  onlineUrl: string | null
  attendanceStatus: string | null
  substituted?: boolean
}

export interface FamilyTodayHomework {
  checklistId: string
  title: string
  instructions: string
  dueDate: string
  maxScore: number | null
  status: string
}

export interface GetFamilyTodayResult {
  date: string
  learnerId: string
  sessions: FamilyTodaySession[]
  homework: FamilyTodayHomework[]
}

export interface GetFamilyWorkInput {
  workspaceId: string
  learnerId: string
}

export interface FamilyWorkAttachment {
  id: string
  name: string
  contentType: string
  size: number
  storagePath: string
  uploadedById: string
  uploadedByName: string
  uploadedAt: string
  downloadUrl: string
}

export interface FamilyWorkItem {
  checklistId: string
  title: string
  instructions: string
  dueDate: string
  maxScore: number | null
  status: 'ASSIGNED' | 'SUBMITTED' | 'LATE' | 'RETURNED' | 'APPROVED'
  feedback: string | null
  score: number | null
  attachments: FamilyWorkAttachment[]
  quiz?: { closed: boolean } | null
}

export interface GetFamilyWorkResult {
  learnerId: string
  work: FamilyWorkItem[]
}

export interface FamilyAttendanceMonth {
  month: string
  present: number
  late: number
  absent: number
  excused: number
  total: number
}

export interface FamilyAttendanceOverall {
  present: number
  late: number
  absent: number
  excused: number
  total: number
}

export interface FamilyProgressAssessmentItem {
  title: string
  maxMarks: number
  score: number | null
  grade?: string | null
}

export interface FamilyProgressMarkItem {
  subjectNodeId: string
  subjectName: string
  termId: string
  termName: string
  assessments: FamilyProgressAssessmentItem[]
}

export interface GetFamilyProgressInput {
  workspaceId: string
  learnerId: string
  from?: string
  to?: string
}

export interface GetFamilyProgressResult {
  attendance: {
    months: FamilyAttendanceMonth[]
    overall: FamilyAttendanceOverall
  }
  marks: FamilyProgressMarkItem[]
}

export async function listMyFamilyLearners(): Promise<ListMyFamilyLearnersResult> {
  return callable<Record<string, never>, ListMyFamilyLearnersResult>('listMyFamilyLearners', {})
}

export async function getFamilyToday(
  input: GetFamilyTodayInput,
): Promise<GetFamilyTodayResult> {
  return callable<GetFamilyTodayInput, GetFamilyTodayResult>('getFamilyToday', input)
}

export async function getFamilyWork(
  input: GetFamilyWorkInput,
): Promise<GetFamilyWorkResult> {
  return callable<GetFamilyWorkInput, GetFamilyWorkResult>('getFamilyWork', input)
}

export async function getFamilyProgress(
  input: GetFamilyProgressInput,
): Promise<GetFamilyProgressResult> {
  return callable<GetFamilyProgressInput, GetFamilyProgressResult>('getFamilyProgress', input)
}

